import React, { useState, useEffect, useMemo } from "react";
import DataTable from "react-data-table-component";
import { FaEdit, FaTrash } from "react-icons/fa";
import config from "../../../config/config";
import { getAuthToken } from "../../../utils/token";
import axios from "axios";
import { toast } from "react-toastify";
import Select from "react-select";
import Loader from "../../loader/Loader";

const customStyles = {
  headCells: {
    style: {
      backgroundColor: "#0d6efd",
      color: "white",
      fontWeight: "bold",
      fontSize: "14px",
    },
  },
};

export default function Users() {
  const [dataList, setDataList] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    name: "",
    username: "",
    email: "",
    role: "",
    password: "",
  });

  const roleOptions = [
    { value: "admin", label: "Admin" },
    { value: "operator", label: "Operator" },
    { value: "manager", label: "Manager" },
    { value: "supervisor", label: "Supervisor" },
    { value: "inspection", label: "Inspection" },
  ];

  useEffect(() => {
    fetchList();
  }, []);

  // FETCH USER LIST
  const fetchList = async () => {
    const token = getAuthToken();

    if (!token) return;

    setListLoading(true);

    try {
      const res = await axios.get(`${config.API}/admin/listusers`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setDataList(res.data?.users || []);
    } catch (err) {
      toast.error("Failed to fetch users");
    } finally {
      setListLoading(false);
    }
  };

  // INPUT CHANGE
  const handleInput = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: null,
    }));
  };

  // SEARCH FILTER
  const filteredData = useMemo(() => {
    const search = filterText.trim().toLowerCase();

    if (!search) {
      return dataList;
    }

    return dataList.filter((user) => {
      return (
        String(user.id || "").toLowerCase().includes(search) ||
        String(user.name || "").toLowerCase().includes(search) ||
        String(user.username || "").toLowerCase().includes(search) ||
        String(user.email || "").toLowerCase().includes(search) ||
        String(user.role || "").toLowerCase().includes(search)
      );
    });
  }, [dataList, filterText]);

  const renderError = (field) =>
    errors[field] ? (
      <div className="text-danger small">
        {errors[field][0]}
      </div>
    ) : null;

  // OPEN ADD MODAL
  const openAddModal = () => {
    setForm({
      name: "",
      username: "",
      email: "",
      role: "",
      password: "",
    });

    setErrors({});
    setIsEditing(false);
    setEditingId(null);
    setShowModal(true);
  };
const logoutUser = async (user_id) => {
  try {
    const token = getAuthToken();

    const res = await axios.post(
      `${config.API}/admin/logoutuser`,
      {
        user_id: user_id,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if(res.data.success){
      toast.success(res.data.message);
    }
  } catch (error) {
    console.error("Logout error:", error);
    throw error;
  }
};
  // OPEN EDIT MODAL
  const openEditModal = (item) => {
    setForm({
      name: item.name || "",
      username: item.username || "",
      email: item.email || "",
      role: item.role || "",
      password: "",
    });

    setErrors({});
    setIsEditing(true);
    setEditingId(item.id);
    setShowModal(true);
  };

  // SUBMIT
  const handleSubmit = async () => {
    const token = getAuthToken();

    if (!token) {
      toast.error("Authentication required");
      return;
    }

    setFormLoading(true);
    setErrors({});

    try {
      let response;

      const payload = {
        name: form.name,
        username: form.username,
        email: form.email,
        role: form.role,
        password: form.password,
        email_verified_at: "2025-01-01 10:00:00",
      };

      if (!isEditing) {
        response = await axios.post(
          `${config.API}/admin/adduser`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        response = await axios.post(
          `${config.API}/admin/updateuser/${editingId}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      toast.success(response?.data?.message || "Success");

      setShowModal(false);
      fetchList();
    } catch (err) {
      console.log("ERROR:", err);

      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else if (err.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error("Something went wrong");
      }
    } finally {
      setFormLoading(false);
    }
  };

  // DELETE
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete?")) {
      return;
    }

    const token = getAuthToken();

    try {
      await axios.delete(
        `${config.API}/admin/deletetraining/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("Deleted successfully");
      fetchList();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  // CLEAR SEARCH
  const clearSearch = () => {
    setFilterText("");
  };

  // COLUMNS
  const columns = [
    {
      name: "ID",
      selector: (row) => row.id,
      width: "70px",
      sortable: true,
    },
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Username",
      selector: (row) => row.username,
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
    },
    {
      name: "Role",
      selector: (row) => row.role,
      sortable: true,
    },
    {
      name: "Logout",
      cell: (row) => (
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-danger"
            onClick={() => logoutUser(row.id)}
          >
            <FaEdit />
          </button>
        </div>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-primary"
            onClick={() => openEditModal(row)}
          >
            <FaEdit />
          </button>

          <button
            className="btn btn-sm btn-danger"
            onClick={() => handleDelete(row.id)}
          >
            <FaTrash />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="container mt-3">
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <strong className="mb-0 fs-4">
          User List
        </strong>

        <div className="d-flex align-items-center">
          {/* SEARCH */}
          <div className="input-group me-2" style={{ width: "300px" }}>
            <input
              type="text"
              placeholder="Search users..."
              className="form-control"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />

            {filterText && (
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={clearSearch}
              >
                ×
              </button>
            )}
          </div>

          {/* ADD USER */}
          <button
            className="btn btn-primary"
            onClick={openAddModal}
          >
            + Add User
          </button>
        </div>
      </div>

      {/* SEARCH RESULT COUNT */}
      {filterText && (
        <div className="mb-2 text-muted">
          Showing {filteredData.length} of {dataList.length} users
        </div>
      )}

      {/* TABLE */}
      {listLoading ? (
        <Loader />
      ) : (
        <DataTable
          columns={columns}
          data={filteredData}
          pagination
          highlightOnHover
          customStyles={customStyles}
          noDataComponent={
            filterText
              ? `No users found for "${filterText}"`
              : "No users found"
          }
        />
      )}

      {/* MODAL */}
      {showModal && (
        <div
          className="modal d-block"
          style={{ background: "rgba(0,0,0,0.45)" }}
        >
          <div className="modal-dialog modal-md">
            <div className="modal-content">

              {/* MODAL HEADER */}
              <div className="modal-header bg-primary text-white">
                <h5>
                  {isEditing ? "Edit User" : "Add User"}
                </h5>

                <button
                  className="btn-close btn-close-white"
                  onClick={() => setShowModal(false)}
                />
              </div>

              {/* MODAL BODY */}
              <div className="modal-body">

                {/* NAME */}
                <label>Name</label>
                <input
                  type="text"
                  name="name"
                  className="form-control mb-1"
                  value={form.name}
                  onChange={handleInput}
                />
                {renderError("name")}

                {/* USERNAME */}
                <label>Username</label>
                <input
                  type="text"
                  name="username"
                  className="form-control mb-1"
                  value={form.username}
                  onChange={handleInput}
                />
                {renderError("username")}

                {/* EMAIL */}
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  className="form-control mb-1"
                  value={form.email}
                  onChange={handleInput}
                />
                {renderError("email")}

                {/* ROLE */}
                {!isEditing && (
                  <>
                    <label>Role</label>

                    <Select
                      options={roleOptions}
                      value={
                        roleOptions.find(
                          (x) => x.value === form.role
                        ) || null
                      }
                      onChange={(sel) => {
                        setForm((prev) => ({
                          ...prev,
                          role: sel?.value || "",
                        }));

                        setErrors((prev) => ({
                          ...prev,
                          role: null,
                        }));
                      }}
                    />

                    {renderError("role")}
                  </>
                )}

                {/* PASSWORD */}
                <label className="mt-2">
                  Password
                </label>

                <input
                  type="password"
                  name="password"
                  className="form-control mb-1"
                  value={form.password}
                  onChange={handleInput}
                />

                {renderError("password")}
              </div>

              {/* MODAL FOOTER */}
              <div className="modal-footer">

                <button
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>

                <button
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={formLoading}
                >
                  {formLoading
                    ? "Saving..."
                    : isEditing
                    ? "Update"
                    : "Save"}
                </button>

              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}